import {
  useMemo,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  useEffect,
} from 'react';
import useSWRInfinite from 'swr/infinite';
import { KeyedMutator } from 'swr';
import queryString from 'query-string';
import { useRecoilValue } from 'recoil';
import { useNotification } from 'hooks/useNotification';
import { get, patch, remove } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { parsePaginatedResponse } from 'lib/utils/parser';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import {
  TouchpointsResponse,
  TouchpointAttributes,
  TouchpointResponse,
} from 'lib/models/touchpoint';

const PER_PAGE_COUNT = 10;

export const JobStateOption = [
  {
    id: '1',
    value: 'All',
    label: 'All',
  },
  {
    id: '2',
    value: 'published',
    label: 'Live',
  },
  {
    id: '3',
    value: 'ready_for_approval',
    label: 'In review',
  },
  {
    id: '4',
    value: 'draft',
    label: 'Drafts',
  },
  {
    id: '5',
    value: 'expired',
    label: 'Closed',
  },
];

export enum JobState {
  LIVE = 'published',
  IN_REVIEW = 'ready_for_approval',
  DRAFTS = 'draft',
  CLOSED = 'expired',
}

export enum TabsType {
  ALL = 0,
  LIVE = 1,
  IN_REVIEW = 2,
  DRAFTS = 3,
  CLOSED = 4,
}

export interface ReturnType {
  touchpoints: Array<TouchpointAttributes>;
  size: number;
  setSize: (size: number) => void;
  hasMoreRecords: boolean;
  isLoading: boolean;
  isLoadingMore: boolean | undefined;
  totalCount: number | undefined;
  isAPILoading: boolean;
  getState: (state: JobState) => string;
  selectedState: TabsType;
  setSelectedState: Dispatch<SetStateAction<TabsType>>;
  onDelete: (
    id: string,
    title: string
  ) => Promise<TouchpointResponse | undefined>;
  onCloseJob: ({
    state,
    enabled,
    id,
    title,
  }: {
    state: JobState;
    enabled: boolean;
    id: string;
    title: string;
  }) => Promise<TouchpointResponse | undefined>;
  mutateCampaigns: KeyedMutator<TouchpointsResponse[]>;
}

export const useJobs = (): ReturnType => {
  const notificationInstance = useNotification();
  const { headers } = useRecoilValue(httpHeadersState);
  const [selectedState, setSelectedState] = useState<TabsType>(TabsType.ALL);

  const getSelectedState = useCallback(() => {
    switch (selectedState) {
      case 0:
        return '';
      case 1:
        return 'published';
      case 2:
        return 'ready_for_approval';
      case 3:
        return 'draft';
      case 4:
        return 'expired';
      default:
    }
  }, [selectedState]);

  const getJobsKey = (pageIndex: number) => {
    const params = {
      page: pageIndex + 1,
      per_page: PER_PAGE_COUNT,
      state: [getSelectedState()],
    };
    const qs = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });

    return [
      `${API_ROUTES.COMPANY_TOUCHPOINTS}?${qs}&sort_by=start_date&sort_order=desc&touchpointable_types[]=Job&touchpointable_types[]=Internship`,
      headers,
    ];
  };

  const {
    data: touchpointsResponse,
    error: companiesError,
    size,
    setSize,
    mutate: mutateCampaigns,
    isLoading: isAPILoading,
  } = useSWRInfinite<TouchpointsResponse>(getJobsKey, get, {
    revalidateFirstPage: false,
  });

  useEffect(() => {
    mutateCampaigns();
  }, [selectedState]);

  const isLoading = !touchpointsResponse && !companiesError;

  const isLoadingMore =
    isLoading ||
    (size > 0 &&
      touchpointsResponse &&
      typeof touchpointsResponse[size - 1] === 'undefined');

  const touchpoints = useMemo(() => {
    return parsePaginatedResponse<TouchpointAttributes>(touchpointsResponse);
  }, [touchpointsResponse]);

  const totalCount = touchpointsResponse
    ? touchpointsResponse[0]?.meta?.total
    : undefined;

  const hasMoreRecords = useMemo(() => {
    const firstApplicationList = Array.isArray(touchpointsResponse)
      ? touchpointsResponse[0]
      : null;
    if (firstApplicationList) {
      return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
    } else {
      return false;
    }
  }, [touchpointsResponse, size]);

  const getState = (state: JobState) => {
    switch (state) {
      case JobState.LIVE:
        return 'Live';
      case JobState.DRAFTS:
        return 'Draft';
      case JobState.IN_REVIEW:
        return 'In review';
      case JobState.CLOSED:
        return 'Closed';
      default:
        return 'Draft';
    }
  };

  const onDelete = async (
    id: string,
    title: string
  ): Promise<TouchpointResponse | undefined> => {
    const path = `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`;
    try {
      const response = await remove<TouchpointResponse>(path, headers);
      mutateCampaigns();
      notificationInstance.success({
        title: 'Success!',
        message: `${title} removed successfully.`,
      });
      return response;
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const onCloseJob = async ({
    state,
    enabled,
    id,
    title,
  }: {
    state: JobState;
    enabled: boolean;
    id: string;
    title: string;
  }): Promise<TouchpointResponse | undefined> => {
    const body = {
      state,
      enabled,
    };
    const path = `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`;
    try {
      const response = await patch<TouchpointResponse>(path, body, headers);
      mutateCampaigns();
      notificationInstance.success({
        title: 'Success!',
        message: `${title} closed successfully.`,
      });
      return response;
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  return {
    touchpoints,
    size,
    setSize,
    hasMoreRecords,
    isLoading,
    isLoadingMore,
    totalCount,
    isAPILoading,
    getState,
    selectedState,
    setSelectedState,
    onDelete,
    onCloseJob,
    mutateCampaigns,
  };
};
